import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { getAlertStatuses, silenceAlert } from '../../services/api.ts';
import { DESC_MAP } from '../../utils/utils.ts';

const BRAND_GREEN = '#1D9E75';

const SILENCE_OPTIONS = [
  { value: 1,   label: '1 hour'  },
  { value: 4,   label: '4 hours' },
  { value: 8,   label: '8 hours' },
  { value: 24,  label: '24 hours'},
  { value: 168, label: '1 week'  },
];

type AlertStatus = {
  id: number;
  name: string;
  measurement_name: string;
  operator: string;
  threshold: number;
  duration_minutes: number;
  is_active: boolean;
  silenced_until: string | null;
  alert_state: {
    breaching_since: string | null;
    last_notified:   string | null;
  } | null;
};

type StatusMeta = { label: string; bg: string; color: string };

function getStatus(alert: AlertStatus): StatusMeta {
  if (!alert.is_active || (alert.silenced_until && new Date(alert.silenced_until) > new Date())) {
    return { label: 'Silenced',   bg: 'rgba(186,117,23,0.25)',  color: '#F0A030' };
  }
  const state = alert.alert_state;
  if (!state?.breaching_since) {
    return { label: 'Dormant',    bg: 'rgba(29,158,117,0.25)',  color: '#2DD4A0' };
  }
  const breachDuration = (Date.now() - new Date(state.breaching_since).getTime()) / 60000;
  if (breachDuration < alert.duration_minutes) {
    return { label: 'Evaluating', bg: 'rgba(186,117,23,0.25)',  color: '#F0A030' };
  }
  return   { label: 'Breaching',  bg: 'rgba(220,60,40,0.25)',   color: '#F05030' };
}

function StatusChip({ alert }: { alert: AlertStatus }) {
  const { label, bg, color } = getStatus(alert);
  return (
    <Box sx={{
      display:      'inline-flex',
      alignItems:   'center',
      alignSelf:    'center',
      px:           1.25,
      py:           0.35,
      borderRadius: '6px',
      background:   bg,
      border:       `1px solid ${color}`,
      fontFamily:   "'DM Mono', monospace",
      fontSize:     11,
      fontWeight:   600,
      color,
      letterSpacing: '0.04em',
      whiteSpace:   'nowrap',
      lineHeight:   1,
    }}>
      {label}
    </Box>
  );
}

const actionBtnSx = (color: string) => ({
  fontFamily:    "'DM Mono', monospace",
  fontSize:      11,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.04em',
  borderColor:   color,
  color,
  borderRadius:  1.5,
  py:            0.4,
  '&:hover': { borderColor: color, background: `${color}18` },
});

export default function AlertStatusGrid() {
  const [rows, setRows]               = useState<AlertStatus[]>([]);
  const [silenceTarget, setSilenceTarget] = useState<AlertStatus | null>(null);
  const [silenceHours, setSilenceHours]   = useState(1);

  const fetchRows = () => {
    getAlertStatuses().then((data: any[]) => {
      setRows(data.map(a => ({
        ...a,
        measurement_name: DESC_MAP[a.measurement?.name] ?? a.measurement?.name ?? '—',
        alert_state: Array.isArray(a.alert_state) ? a.alert_state[0] ?? null : a.alert_state,
      })));
    });
  };

  useEffect(() => {
    fetchRows();
    const id = setInterval(fetchRows, 30_000);
    return () => clearInterval(id);
  }, []);

  const handleSilenceConfirm = async () => {
    if (!silenceTarget) return;
    const until = new Date(Date.now() + silenceHours * 3_600_000).toISOString();
    await silenceAlert(silenceTarget.id, until, false);
    setSilenceTarget(null);
    fetchRows();
  };

  const handleUnsilence = async (alert: AlertStatus) => {
    await silenceAlert(alert.id, null, true);
    fetchRows();
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Alert Name',
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: p => (
        <Typography sx={{ fontFamily: "'Syne', sans-serif", fontSize: 13, fontWeight: 500, color: 'var(--color-text-primary)' }}>
          {p.value}
        </Typography>
      ),
    },
    {
      field: 'measurement_name',
      headerName: 'Parameter',
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: p => (
        <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: 'var(--color-text-secondary)' }}>
          {p.value}
        </Typography>
      ),
    },
    {
      field: 'condition',
      headerName: 'Condition',
      flex: 0.75,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (_v, row) => `${row.operator} ${row.threshold}`,
      renderCell: p => (
        <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: 'var(--color-text-secondary)' }}>
          {p.value}
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.75,
      align: 'center',
      headerAlign: 'center',
      renderCell: p => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
          <StatusChip alert={p.row} />
        </Box>
      ),
    },
    {
      field: 'silenced_until',
      headerName: 'Silenced Until',
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: p => (
        <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: 'var(--color-text-secondary)' }}>
          {p.value ? new Date(p.value).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.75,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: p => {
        const isSilenced = !p.row.is_active ||
          (p.row.silenced_until && new Date(p.row.silenced_until) > new Date());
        return isSilenced ? (
          <Button size="small" variant="outlined" onClick={() => handleUnsilence(p.row)}
            sx={actionBtnSx(BRAND_GREEN)}>
            Unsilence
          </Button>
        ) : (
          <Button size="small" variant="outlined" onClick={() => setSilenceTarget(p.row)}
            sx={actionBtnSx('#BA7517')}>
            Silence
          </Button>
        );
      },
    },
  ];

  return (
    <>
      <DataGrid
        rows={rows}
        columns={columns}
        pageSizeOptions={[10, 25, { value: -1, label: 'All' }]}
        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        disableRowSelectionOnClick
        sx={{
          border: 'none',
          background: 'transparent',
          '& .MuiDataGrid-columnHeader': {
            background: `${BRAND_GREEN} !important`,
            color: '#ffffff !important',
            fontFamily: "'Syne', sans-serif",
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            color: '#ffffff !important',
          },
          '& .MuiDataGrid-columnSeparator': { display: 'none' },
          '& .MuiDataGrid-row': {
            borderBottom: '1px solid rgba(255,255,255,0.04)',
            background: 'transparent',
          },
          '& .MuiDataGrid-row:hover': {
            background: 'rgba(255,255,255,0.02)',
          },
          '& .MuiDataGrid-cell': {
            border: 'none',
            background: 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          },
          '& .MuiDataGrid-columnHeaderTitleContainer': {
            justifyContent: 'center',
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: '1px solid rgba(255,255,255,0.06)',
            background: 'transparent',
            fontFamily: "'DM Mono', monospace",
            fontSize: 11,
          },
          '& .MuiTablePagination-root': {
            fontFamily: "'DM Mono', monospace",
            fontSize: 11,
            color: 'var(--color-text-secondary)',
          },
          '& .MuiDataGrid-sortIcon': {
            color: '#ffffff !important',
          },
          '& .MuiDataGrid-menuIconButton': {
            color: '#ffffff !important',
          },
        }}
      />

      {/* Silence dialog */}
      <Dialog
        open={Boolean(silenceTarget)}
        onClose={() => setSilenceTarget(null)}
        PaperProps={{
          sx: {
            background:   'var(--color-bg-secondary)',
            borderRadius: 3,
            p:            1,
            minWidth:     320,
          },
        }}
      >
        <DialogTitle sx={{ fontFamily: "'Syne', sans-serif", fontSize: 16, fontWeight: 600, color: 'var(--color-text-primary)' }}>
          Silence: {silenceTarget?.name}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: 'var(--color-text-secondary)', mb: 2 }}>
            Select how long to silence this alert:
          </Typography>
          <Select
            fullWidth
            size="small"
            value={silenceHours}
            onChange={(e: SelectChangeEvent<number>) => setSilenceHours(Number(e.target.value))}
            sx={{
              fontFamily: "'DM Mono', monospace",
              fontSize: 13,
              borderRadius: 2,
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.12)' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: BRAND_GREEN },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: BRAND_GREEN },
            }}
          >
            {SILENCE_OPTIONS.map(o => (
              <MenuItem key={o.value} value={o.value} sx={{ fontFamily: "'DM Mono', monospace", fontSize: 13 }}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setSilenceTarget(null)}
            sx={{ fontFamily: "'DM Mono', monospace", fontSize: 11, textTransform: 'uppercase', color: 'var(--color-text-secondary)', borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSilenceConfirm}
            sx={{
              fontFamily: "'DM Mono', monospace",
              fontSize: 11,
              textTransform: 'uppercase',
              background: '#BA7517',
              borderRadius: 2,
              '&:hover': { background: '#9a5c10' },
            }}
          >
            Silence
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
